import { Helmet } from 'react-helmet-async';
import ContactForm from '../components/ContactForm';
import GalleryLightbox from '../components/GalleryLightbox';

export default function CustomJewelryPage() {
  const steps = [
    { step: "01", title: "IDÉ & INSIRATION", desc: "Du har en tanke, en skitse eller et gammelt smykke, du ønsker omdannet til et nyt unikt design." },
    { step: "02", title: "PERSONLIG DIALOG", desc: "Vi drøfter dine ønsker til metal, ædelsten, form og budget sammen i vor forretning." },
    { step: "03", title: "DESIGN & TEGNING", desc: "Vi udarbejder forslag og målsætninger, der sikrer, at smykket udtrykker din personlighed." },
    { step: "04", title: "FREMSTILLING PÅ VÆRKSTEDET", desc: "Vores erfarne guldsmed støber, fatter og former smykket fra bunden med stor præcision." },
    { step: "05", title: "FÆRDIGT SMYKKE", desc: "Du modtager dit færdige unika smykke – lige til at bære eller give som en uforglemmelig gave." },
  ];

  return (
    <>
      <Helmet>
        <title>Smykker efter eget design - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Få fremstillet unika smykker og specialdesign efter dine ønsker hos Brønshøj Ure & Guld i Brønshøj."
        />
        <meta property="og:title" content="Smykker efter eget design - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Få fremstillet unika smykker og specialdesign efter dine ønsker hos Brønshøj Ure & Guld i Brønshøj."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/img-2724-1-1024x682.jpg"
        />
      </Helmet>

      {/* Hero Header */}
      <section className="bg-[#0B132B] text-white pt-36 sm:pt-40 lg:pt-56 pb-16 sm:pb-20 lg:pb-28 border-b border-white/10 text-center">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] font-semibold block">
            SPECIALFREMSTILLING & UNIKA
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white">
            SMYKKER EFTER EGET DESIGN
          </h1>
          <p className="text-xs sm:text-sm text-stone-300 max-w-2xl mx-auto uppercase tracking-widest font-sans leading-relaxed">
            Hos Brønshøj Ure & Guld designer og fremstiller vi egne smykker og kan skabe smykker helt efter dine specifikke ønsker og drømme.
          </p>
        </div>
      </section>

      {/* Main Section */}
      <section className="py-20 bg-[#F9F8F6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-[#F2EFE9] border border-[#E2DDD3] p-10 sm:p-14 space-y-6">
              <div className="w-[1px] h-8 bg-[#B5A897]"></div>
              <h2 className="font-serif text-2xl sm:text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                FRA SPÆD IDÉ TIL ET UNIKT SMYKKE
              </h2>
              <div className="text-xs sm:text-sm text-stone-700 leading-relaxed space-y-4">
                <p>
                  Et personligt smykke bærer på historier, følelser og minder. Hvad end det gælder vielsesringe, en særlig mærkedagsgave eller genanvendelse af eget arveguld til et nyt moderne smykke, er muligheden for specialdesign fantastisk.
                </p>
                <p>
                  Vi tilbyder et bredt udvalg af guld og unikke smykker fra eget værksted. Har du en idé til dit eget unikke smykke, står vi klar til at hjælpe dig fra første tanke til færdigt håndværk.
                </p>
              </div>

              <div className="pt-2">
                <a
                  href="#dialog-form"
                  className="inline-block border border-[#0B132B] text-[#0B132B] bg-transparent hover:bg-[#0B132B] hover:text-white transition-all text-xs tracking-[0.25em] uppercase px-8 py-3.5 rounded-none font-medium"
                >
                  TAL MED OS OM DIT EGET SMYKKE
                </a>
              </div>
            </div>

            <div className="border border-[#E2DDD3]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/img-2724-1-1024x682.jpg"
                alt="Smykkedesign fremstilling"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Process steps */}
          <div>
            <div className="text-center max-w-xl mx-auto mb-12 space-y-2">
              <div className="w-[1px] h-8 bg-[#B5A897] mx-auto mb-3"></div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                FAGLIG FREMGANGSMÅDE
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                ARBEJDSPROCESSEN VED SPECIALDESIGN
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {steps.map((item) => (
                <div key={item.step} className="bg-[#F2EFE9] p-6 border border-[#E2DDD3] text-center space-y-2 rounded-none shadow-none">
                  <span className="text-2xl font-serif text-[#B5A897] block">{item.step}</span>
                  <h3 className="font-serif text-sm font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">{item.title}</h3>
                  <p className="text-[11px] text-stone-600 leading-relaxed pt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CMS Gallery */}
          <GalleryLightbox gallerySlug="specialdesign" title="GLIMT AF EGNE DESIGNS & UNIKA SMYKKER" />

          {/* Contact Banner */}
          <div id="dialog-form" className="w-full pt-4">
            <ContactForm />
          </div>

        </div>
      </section>
    </>
  );
}
